import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export function ConsentNotice() {
  const { user, setUser, logout } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || user.terms_current) return null;

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/accept-terms");
      setUser(data);
      toast.success("Agreement recorded");
    } catch (e) {
      toast.error(apiError(e, "Could not record your agreement"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end bg-brand-ink/45 backdrop-blur-[2px] animate-fade-in"
      data-testid="consent-notice"
    >
      <div className="w-full border-t-2 border-brand-red bg-white shadow-2xl">
        <div className="mx-auto flex max-w-5xl flex-col gap-5 px-5 py-6 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-red/10 text-brand-red">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <p className="font-heading text-base font-bold text-brand-ink">
                I agree to the Terms of Use
              </p>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-brand-mute">
                TL-Thunderbird processes voter and contact data on this campaign's behalf. By
                continuing you acknowledge our{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-brand-red underline decoration-brand-red/30 underline-offset-2 hover:decoration-brand-red"
                  data-testid="consent-privacy-link"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-brand-red underline decoration-brand-red/30 underline-offset-2 hover:decoration-brand-red"
                  data-testid="consent-terms-link"
                >
                  Terms of Use
                </a>
                {user.terms_label ? ` (${user.terms_label})` : ""}. Your agreement is recorded
                with a timestamp for the campaign's compliance record.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3 lg:flex-col lg:items-stretch">
            <Button
              onClick={accept}
              disabled={busy}
              className="bg-brand-red text-white transition-transform hover:-translate-y-0.5 hover:bg-brand-redhover"
              data-testid="consent-accept-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} I agree
            </Button>
            <button
              onClick={logout}
              className="text-xs font-medium text-brand-mute transition-colors hover:text-brand-red"
              data-testid="consent-signout-btn"
            >
              Sign out instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
